import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { LEGAL_PAGES } from "@/lib/content";
import { Link, useParams } from "wouter";

/**
 * Render an intro paragraph that may contain the literal phrase
 * "Trust page" — when present, the phrase becomes a clickable link
 * to `/trust`. Used by the `/legal/security` page so security
 * reviewers can self-serve to the trust portal in one click instead
 * of having to copy a URL out of body copy.
 */
function renderIntroWithTrustLink(intro: string) {
  const phrase = "Trust page";
  const idx = intro.indexOf(phrase);
  if (idx === -1) {
    return intro;
  }
  const before = intro.slice(0, idx);
  const after = intro.slice(idx + phrase.length);
  return (
    <>
      {before}
      <Link
        href="/trust"
        data-testid="link-legal-security-trust"
        className="text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
      >
        {phrase}
      </Link>
      {after}
    </>
  );
}

export default function LegalPage() {
  const { slug } = useParams<{ slug: string }>();
  
  // Find the legal page data matching the slug
  const pageKey = Object.keys(LEGAL_PAGES).find(key => key === slug) as keyof typeof LEGAL_PAGES | undefined;
  const pageData = pageKey ? LEGAL_PAGES[pageKey] : null;

  if (!pageData) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-h1 mb-4">Legal Document Not Found</h1>
          <p className="text-body-lg text-slate-500">The legal document you are looking for does not exist.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead title={pageData.seo.title} description={pageData.seo.description} />
      
      <article className="pt-24 pb-32">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <header className="mb-12 border-b border-border pb-8">
            <h1 className="text-display mb-4 text-navy dark:text-white">
              {pageData.title}
            </h1>
            <div className="text-sm text-muted-foreground">
              {pageData.updated}
            </div>
          </header>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-8">
              {slug === "security"
                ? renderIntroWithTrustLink(pageData.intro)
                : pageData.intro}
            </p>
            {pageData.sections.map((section: { heading: string; body: string }, i: number) => (
              <div key={i} className="mb-8">
                <h2 className="text-xl font-bold text-navy dark:text-white mb-4">{section.heading}</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {section.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </article>
    </Layout>
  );
}
